import { useState, useMemo, useCallback, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertTriangle, Check, Image, Video, X,
  ChevronLeft, ChevronRight, Plus, Minus, ArrowLeft, ArrowRight,
  Layers, BookOpen, Heart, Star, ShieldCheck, ShoppingBag,
  RefreshCw, Sparkles, Loader2, Wand2,
  BarChart3, RefreshCcw,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import type {
  AdUnitSelection, AdUnitPlan, FunnelStrategyData,
  MediaMixConfig, WizardAdUnit, CampaignDraft, MediaAllocation,
} from "@/lib/campaign-draft";
import type { Brand } from "@shared/schema";
import { type AdImageRecord, displayUrl, normalizeExampleImage } from "@shared/adImage";

function exampleImageUrls(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((e) => displayUrl(normalizeExampleImage(e)))
    .filter((u): u is string => !!u);
}

// ─── Stage config ─────────────────────────────────────────────────────────────

const STAGES = [
  {
    id: "Awareness",
    label: "Awareness",
    shortLabel: "Awareness",
    subtitle: "Attention & Discovery",
    description: "Formats that introduce your brand and capture attention at scale.",
    Icon: Layers,
    color: "violet",
    textColor: "text-violet-600 dark:text-violet-400",
    bgTint: "bg-violet-500/[0.04] border-violet-500/15",
    iconBg: "bg-violet-500/15",
    badge: "bg-violet-500/10 text-violet-700 border-violet-500/20 dark:text-violet-400",
    allocBg: "bg-violet-500/8",
  },
  {
    id: "Knowledge",
    label: "Knowledge",
    shortLabel: "Knowledge",
    subtitle: "Education & Understanding",
    description: "Formats that educate and build deeper understanding of your product.",
    Icon: BookOpen,
    color: "blue",
    textColor: "text-blue-600 dark:text-blue-400",
    bgTint: "bg-blue-500/[0.04] border-blue-500/15",
    iconBg: "bg-blue-500/15",
    badge: "bg-blue-500/10 text-blue-700 border-blue-500/20 dark:text-blue-400",
    allocBg: "bg-blue-500/8",
  },
  {
    id: "Liking",
    label: "Liking",
    shortLabel: "Liking",
    subtitle: "Emotional Connection",
    description: "Formats that build emotional resonance and positive brand sentiment.",
    Icon: Heart,
    color: "sky",
    textColor: "text-sky-600 dark:text-sky-400",
    bgTint: "bg-sky-500/[0.04] border-sky-500/15",
    iconBg: "bg-sky-500/15",
    badge: "bg-sky-500/10 text-sky-700 border-sky-500/20 dark:text-sky-400",
    allocBg: "bg-sky-500/8",
  },
  {
    id: "Preference",
    label: "Preference",
    shortLabel: "Preference",
    subtitle: "Competitive Advantage",
    description: "Formats that differentiate your brand and build preference over competitors.",
    Icon: Star,
    color: "indigo",
    textColor: "text-indigo-600 dark:text-indigo-400",
    bgTint: "bg-indigo-500/[0.04] border-indigo-500/15",
    iconBg: "bg-indigo-500/15",
    badge: "bg-indigo-500/10 text-indigo-700 border-indigo-500/20 dark:text-indigo-400",
    allocBg: "bg-indigo-500/8",
  },
  {
    id: "Conviction",
    label: "Conviction",
    shortLabel: "Conviction",
    subtitle: "Trust & Proof",
    description: "Formats that build trust through social proof, testimonials, and validation.",
    Icon: ShieldCheck,
    color: "amber",
    textColor: "text-amber-600 dark:text-amber-400",
    bgTint: "bg-amber-500/[0.04] border-amber-500/15",
    iconBg: "bg-amber-500/15",
    badge: "bg-amber-500/10 text-amber-700 border-amber-500/20 dark:text-amber-400",
    allocBg: "bg-amber-500/8",
  },
  {
    id: "Purchase",
    label: "Purchase",
    shortLabel: "Purchase",
    subtitle: "Conversion & Action",
    description: "Formats that drive the final decision and close the sale.",
    Icon: ShoppingBag,
    color: "emerald",
    textColor: "text-emerald-600 dark:text-emerald-400",
    bgTint: "bg-emerald-500/[0.04] border-emerald-500/15",
    iconBg: "bg-emerald-500/15",
    badge: "bg-emerald-500/10 text-emerald-700 border-emerald-500/20 dark:text-emerald-400",
    allocBg: "bg-emerald-500/8",
  },
];

// ─── helpers ──────────────────────────────────────────────────────────────────

function realToWizardUnit(real: any): WizardAdUnit {
  const stages: string[] = [];
  const funnelStage = real.funnelStage || "";
  if (funnelStage) stages.push(funnelStage);
  if (stages.length === 0) stages.push("Awareness");
  return {
    id: real.id,
    name: real.name,
    format: real.formatType === "video" ? "video" : "static",
    category: real.formatType === "video" ? "Video" : "Static",
    description: real.objective || real.strategicRole?.slice(0, 120) || "",
    supported_funnel_stages: stages,
    recommended_use_case: real.strategicRole?.slice(0, 200) || "",
    exampleImages: exampleImageUrls(real.exampleImages),
  };
}

// ─── Detail modal ─────────────────────────────────────────────────────────────

function AdUnitDetailModal({
  raw, isSelected, onToggle, onClose, stageConfig,
  selectedImageUrl, onSelectImage,
}: {
  raw: any | null;
  isSelected: boolean;
  onToggle: () => void;
  onClose: () => void;
  stageConfig: typeof STAGES[number] | null;
  selectedImageUrl?: string | null;
  onSelectImage?: (url: string) => void;
}) {
  const images: string[] = raw ? exampleImageUrls(raw.exampleImages) : [];
  const initialIdx = useMemo(() => {
    if (selectedImageUrl) {
      const i = images.indexOf(selectedImageUrl);
      if (i >= 0) return i;
    }
    return 0;
  }, [selectedImageUrl, images]);
  const [imgIndex, setImgIndex] = useState(initialIdx);
  useEffect(() => { setImgIndex(initialIdx); }, [initialIdx, raw?.id]);
  if (!raw) return null;
  const currentImage = images[imgIndex] ?? null;
  const isCurrentSelected = !!currentImage && currentImage === selectedImageUrl;
  const isVideo = raw.formatType === "video";
  const drivers: string[] = Array.isArray(raw.structuralDrivers) ? raw.structuralDrivers : [];
  const psychFn = raw.psychologicalFunction as Record<string, string> | null;
  const deployment = raw.funnelDeployment as { primary?: string; objective?: string; secondary?: string } | null;

  return (
    <Dialog open={!!raw} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0 gap-0" data-testid="modal-ad-unit-detail">
        <div className="relative w-full bg-muted/30 overflow-hidden" style={{ minHeight: images.length > 0 ? "260px" : "80px" }}>
          {images.length > 0 ? (
            <>
              <img src={images[imgIndex]} alt={`${raw.name} example ${imgIndex + 1}`} className="w-full object-contain max-h-64" />
              {isSelected && onSelectImage && currentImage && (
                <div className="absolute top-2 right-2 z-10">
                  {isCurrentSelected ? (
                    <Badge className="rounded-full bg-primary text-primary-foreground border-primary text-[10px] gap-1 px-2 py-0.5" data-testid="badge-image-selected">
                      <Check className="h-3 w-3" /> Reference
                    </Badge>
                  ) : (
                    <Button
                      size="sm"
                      variant="default"
                      onClick={(e) => { e.stopPropagation(); onSelectImage(currentImage); }}
                      className="h-7 rounded-full text-[11px] px-2.5 gap-1 shadow-md"
                      data-testid="btn-use-this-image"
                    >
                      <Check className="h-3 w-3" /> Use this image
                    </Button>
                  )}
                </div>
              )}
              {images.length > 1 && (
                <>
                  <button onClick={(e) => { e.stopPropagation(); setImgIndex((i) => (i - 1 + images.length) % images.length); }}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-background/80 backdrop-blur-sm border flex items-center justify-center hover:bg-background"
                    data-testid="btn-gallery-prev">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); setImgIndex((i) => (i + 1) % images.length); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-background/80 backdrop-blur-sm border flex items-center justify-center hover:bg-background"
                    data-testid="btn-gallery-next">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1">
                    {images.map((_, i) => (
                      <button key={i} onClick={(e) => { e.stopPropagation(); setImgIndex(i); }}
                        className={`rounded-full transition-all ${i === imgIndex ? "w-4 h-1.5 bg-primary" : "w-1.5 h-1.5 bg-white/50"}`} />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-16">
              {isVideo ? <Video className="h-8 w-8 text-muted-foreground/30" /> : <Image className="h-8 w-8 text-muted-foreground/30" />}
            </div>
          )}
        </div>

        <div className="p-6 space-y-5">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-2">
                <DialogTitle className="text-lg font-bold">{raw.name}</DialogTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  {stageConfig && (
                    <Badge variant="outline" className={`text-xs px-2 py-0.5 rounded-full border ${stageConfig.badge}`}>
                      {stageConfig.label}
                    </Badge>
                  )}
                  <Badge variant="secondary" className="text-xs px-2 py-0.5 rounded-full capitalize">
                    {isVideo ? "Video" : "Static"}
                  </Badge>
                  {raw.platform && <Badge variant="outline" className="text-xs px-2 py-0.5 rounded-full">{raw.platform}</Badge>}
                </div>
              </div>
              <Button onClick={onToggle} size="sm" variant={isSelected ? "outline" : "default"}
                className="flex-shrink-0 rounded-full gap-1.5" data-testid="btn-modal-toggle-selection">
                {isSelected ? <><Minus className="h-3.5 w-3.5" />Remove</> : <><Plus className="h-3.5 w-3.5" />Add</>}
              </Button>
            </div>
          </DialogHeader>

          {raw.objective && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Objective</p>
              <p className="text-sm font-medium">{raw.objective}</p>
            </div>
          )}
          {raw.strategicRole && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Strategic Role</p>
              <p className="text-sm text-muted-foreground leading-relaxed">{raw.strategicRole}</p>
            </div>
          )}
          {deployment && (deployment.primary || deployment.secondary) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {deployment.primary && (
                <div className="rounded-xl border bg-muted/30 px-3 py-2.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">Primary Use</p>
                  <p className="text-xs font-medium">{deployment.primary}</p>
                </div>
              )}
              {deployment.secondary && (
                <div className="rounded-xl border bg-muted/30 px-3 py-2.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5">Secondary Use</p>
                  <p className="text-xs font-medium">{deployment.secondary}</p>
                </div>
              )}
            </div>
          )}
          {psychFn && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">How It Works Psychologically</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {["attention", "belief", "desire", "action"].map((key) =>
                  psychFn[key] ? (
                    <div key={key} className="rounded-xl border bg-muted/20 px-3 py-2.5">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-primary/70 mb-0.5 capitalize">{key}</p>
                      <p className="text-xs text-muted-foreground leading-relaxed">{psychFn[key]}</p>
                    </div>
                  ) : null
                )}
              </div>
            </div>
          )}
          {drivers.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Structural Drivers</p>
              <ul className="space-y-1.5">
                {drivers.map((d, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground leading-relaxed">
                    <span className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-primary/40" />{d}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="pt-2 border-t">
            <Button onClick={onToggle} className="w-full rounded-full gap-2" variant={isSelected ? "outline" : "default"}
              data-testid="btn-modal-toggle-selection-bottom">
              {isSelected ? <><Check className="h-4 w-4" />Added — Click to Remove</> : <><Plus className="h-4 w-4" />Add to Campaign</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Stage stepper ─────────────────────────────────────────────────────────────

function StageStepper({
  activeIndex, selectionsByStage, onNavigate,
}: {
  activeIndex: number;
  selectionsByStage: Record<string, number>;
  onNavigate: (idx: number) => void;
}) {
  return (
    <div className="flex items-center gap-0" data-testid="stage-stepper">
      {STAGES.map((stage, idx) => {
        const isActive = idx === activeIndex;
        const isDone = idx < activeIndex;
        const count = selectionsByStage[stage.id] ?? 0;
        const Icon = stage.Icon;

        return (
          <div key={stage.id} className="flex items-center flex-1 min-w-0">
            <button
              onClick={() => onNavigate(idx)}
              className={`group flex-1 flex flex-col items-center gap-1 px-1 py-2.5 rounded-lg transition-all ${
                isActive
                  ? "bg-primary/[0.07] ring-1 ring-primary/20"
                  : isDone
                  ? "hover:bg-muted/50"
                  : "hover:bg-muted/30 opacity-60"
              }`}
              data-testid={`btn-stage-${stage.id}`}
            >
              <div className={`w-7 h-7 rounded-md flex items-center justify-center transition-all ${
                isActive
                  ? "bg-primary text-primary-foreground"
                  : isDone && count > 0
                  ? "bg-primary/15 text-primary"
                  : "bg-muted text-muted-foreground"
              }`}>
                {isDone && count > 0 ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <Icon className="h-3.5 w-3.5" />
                )}
              </div>
              <div className="text-center">
                <p className={`text-[9px] font-semibold tracking-wider ${
                  isActive ? "text-primary" : "text-muted-foreground"
                }`}>{stage.shortLabel}</p>
                <p className={`text-[9px] font-medium ${
                  count > 0 ? "text-primary/80" : "text-muted-foreground/40"
                }`} data-testid={`text-stage-count-${stage.id}`}>
                  {count} selected
                </p>
              </div>
            </button>
            {idx < STAGES.length - 1 && (
              <div className={`h-px w-2 shrink-0 transition-colors ${
                idx < activeIndex ? "bg-primary/30" : "bg-border"
              }`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Ad unit card ─────────────────────────────────────────────────────────────

function AdUnitCard({
  raw, isSelected, onToggle, onPreview, stageConfig, isSecondary,
  selectedImageUrl,
}: {
  raw: any;
  isSelected: boolean;
  onToggle: () => void;
  onPreview: () => void;
  stageConfig: typeof STAGES[number];
  isSecondary?: boolean;
  selectedImageUrl?: string | null;
}) {
  const images: string[] = exampleImageUrls(raw.exampleImages);
  const thumb = selectedImageUrl || images[0] || null;
  const isVideo = raw.formatType === "video";

  return (
    <Card
      onClick={onToggle}
      className={`group relative cursor-pointer transition-all overflow-hidden ${
        isSelected
          ? "border-primary/50 bg-primary/[0.03] ring-1 ring-primary/25"
          : "hover:border-border/80 hover:shadow-sm"
      }`}
      data-testid={`card-ad-unit-${raw.id}`}
    >
      <div className="flex gap-0">
        {/* Thumbnail */}
        <div className="relative w-20 min-h-[6rem] shrink-0 overflow-hidden bg-muted/40">
          {thumb ? (
            <img src={thumb} alt={raw.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              {isVideo ? <Video className="h-5 w-5 text-muted-foreground/30" /> : <Image className="h-5 w-5 text-muted-foreground/30" />}
            </div>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onPreview(); }}
            className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-all opacity-0 group-hover:opacity-100"
            data-testid={`btn-preview-${raw.id}`}
            title="View details"
          >
            <ChevronRight className="h-4 w-4 text-white drop-shadow" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold leading-tight" data-testid={`text-ad-unit-name-${raw.id}`}>
                {raw.name}
              </p>
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 rounded capitalize">
                  {isVideo ? "Video" : "Static"}
                </Badge>
                {raw.platform && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 rounded">
                    {raw.platform}
                  </Badge>
                )}
                {isSecondary && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 rounded border-dashed text-muted-foreground">
                    2nd funnel
                  </Badge>
                )}
              </div>
            </div>
            <div className={`flex-shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
              isSelected
                ? "bg-primary border-primary text-primary-foreground"
                : "border-muted-foreground/30 group-hover:border-primary/50"
            }`}>
              {isSelected && <Check className="h-3 w-3" />}
            </div>
          </div>
          {raw.objective && (
            <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed line-clamp-2">
              {raw.objective}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}

// ─── Summary view ─────────────────────────────────────────────────────────────

function SelectionSummary({
  allAdUnits, adUnitSelections, onEdit,
}: {
  allAdUnits: any[];
  adUnitSelections: AdUnitSelection[];
  onEdit: (stageIdx: number) => void;
}) {
  // Index ad units by id so we can look up presentation data (image, name,
  // format) for each selection. The source of truth for *which stage a unit
  // belongs to* is the selection's own funnelStageId, NOT the ad unit's
  // intrinsic funnelStage/secondaryFunnel — picking a unit for one stage
  // must not cause it to appear under other stages.
  const adUnitsById = useMemo(() => {
    const m = new Map<string, any>();
    for (const u of allAdUnits) m.set(u.id, u);
    return m;
  }, [allAdUnits]);

  const selectionsByStage = useMemo(() => {
    const m = new Map<string, AdUnitSelection[]>();
    for (const sel of adUnitSelections) {
      if (!m.has(sel.funnelStageId)) m.set(sel.funnelStageId, []);
      m.get(sel.funnelStageId)!.push(sel);
    }
    return m;
  }, [adUnitSelections]);

  return (
    <div className="space-y-4" data-testid="selection-summary">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-base">Selection Summary</h3>
          <p className="text-sm text-muted-foreground mt-0.5">{adUnitSelections.length} ad units selected across all stages</p>
        </div>
      </div>

      {STAGES.map((stage, idx) => {
        const stageSelections = (selectionsByStage.get(stage.id) || [])
          .map((sel) => ({ sel, raw: adUnitsById.get(sel.adUnitId) }))
          .filter((x) => x.raw);
        const Icon = stage.Icon;

        return (
          <Card key={stage.id} className="overflow-hidden" data-testid={`summary-stage-${stage.id}`}>
            <div className={`px-4 py-3 flex items-center justify-between border-b ${
              stageSelections.length > 0 ? "bg-primary/[0.03]" : "bg-muted/30"
            }`}>
              <div className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-md flex items-center justify-center ${
                  stageSelections.length > 0 ? "bg-primary/15" : "bg-muted"
                }`}>
                  {stageSelections.length > 0 ? (
                    <Check className="h-3 w-3 text-primary" />
                  ) : (
                    <Icon className="h-3 w-3 text-muted-foreground" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-semibold">{stage.label}</p>
                  <p className="text-[11px] text-muted-foreground">{stage.subtitle}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {stageSelections.length > 0 && (
                  <Badge className="text-xs rounded-full bg-primary/10 text-primary border-primary/20 font-medium">
                    {stageSelections.length}
                  </Badge>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit(idx)}
                  className="h-7 px-2.5 text-xs rounded-full gap-1"
                  data-testid={`btn-edit-stage-${stage.id}`}
                >
                  <RefreshCw className="h-3 w-3" />
                  {stageSelections.length === 0 ? "Add" : "Edit"}
                </Button>
              </div>
            </div>

            {stageSelections.length > 0 ? (
              <div className="divide-y divide-border">
                {stageSelections.map(({ sel, raw }) => {
                  const isVideo = raw?.formatType === "video";
                  const thumb = sel?.selectedImageUrl || exampleImageUrls(raw?.exampleImages)[0] || null;
                  return (
                    <div key={`${stage.id}-${raw.id}`} className="flex items-center gap-3 px-4 py-2.5" data-testid={`summary-item-${stage.id}-${raw.id}`}>
                      <div className="w-8 h-8 rounded-md overflow-hidden bg-muted/40 flex-shrink-0">
                        {thumb ? (
                          <img src={thumb} alt={raw.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            {isVideo ? <Video className="h-3.5 w-3.5 text-muted-foreground/40" /> : <Image className="h-3.5 w-3.5 text-muted-foreground/40" />}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">{raw.name}</p>
                        <p className="text-[11px] text-muted-foreground capitalize">{isVideo ? "Video" : "Static"}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="px-4 py-3">
                <p className="text-xs text-muted-foreground italic">No ad units selected for this stage</p>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface AdUnitSelectionStepProps {
  brandId: string;
  brand: Brand | null;
  draft: CampaignDraft;
  adUnitSelections: AdUnitSelection[];
  adUnitPlan: AdUnitPlan | null;
  funnelStrategy: FunnelStrategyData | null;
  mediaMix: MediaMixConfig | null;
  mediaAllocation: MediaAllocation | null;
  onUpdateSelections: (selections: AdUnitSelection[]) => void;
  onUpdatePlan: (plan: AdUnitPlan | null) => void;
  onUpdateMediaMix: (mix: MediaMixConfig) => void;
  onUpdateMediaAllocation: (allocation: MediaAllocation | null) => void;
  isStale: boolean;
  onClearStale: () => void;
}

// ─── Main ─────────────────────────────────────────────────────────────────────

const DEFAULT_STATIC_UNITS = 20;

export function AdUnitSelectionStep({
  brandId,
  draft,
  adUnitSelections,
  mediaMix,
  mediaAllocation,
  onUpdateSelections,
  onUpdateMediaMix,
  onUpdateMediaAllocation,
  isStale,
  onClearStale,
}: AdUnitSelectionStepProps) {
  const { toast } = useToast();
  const [stageIndex, setStageIndex] = useState(0);

  // Media mix is now embedded at the top of this step. Default to 20 static
  // units the first time the user lands here so downstream steps always have
  // a value to work with; the user can adjust it in place.
  useEffect(() => {
    if (!mediaMix) {
      onUpdateMediaMix({
        totalUnits: DEFAULT_STATIC_UNITS,
        staticUnits: DEFAULT_STATIC_UNITS,
        videoUnits: 0,
      });
    }
  }, [mediaMix, onUpdateMediaMix]);

  const savedStaticUnits = mediaMix?.staticUnits ?? mediaMix?.totalUnits ?? DEFAULT_STATIC_UNITS;
  const [staticUnitsDraft, setStaticUnitsDraft] = useState<string>(String(savedStaticUnits));
  useEffect(() => {
    setStaticUnitsDraft(String(savedStaticUnits));
  }, [savedStaticUnits]);
  const parsedDraft = parseInt(staticUnitsDraft, 10);
  const isDraftValid = Number.isFinite(parsedDraft) && parsedDraft > 0;
  const isDraftDirty = isDraftValid && parsedDraft !== savedStaticUnits;
  const handleSaveStaticUnits = () => {
    if (!isDraftValid) return;
    onUpdateMediaMix({ totalUnits: parsedDraft, staticUnits: parsedDraft, videoUnits: 0 });
  };
  const [showSummary, setShowSummary] = useState(false);
  const [showAllUnits, setShowAllUnits] = useState(false);
  const [smartPickMessage, setSmartPickMessage] = useState<string | null>(null);
  const [previewUnit, setPreviewUnit] = useState<any | null>(null);
  const [isGeneratingAllocation, setIsGeneratingAllocation] = useState(false);

  const handleGenerateAllocation = useCallback(async () => {
    setIsGeneratingAllocation(true);
    try {
      // Group the user's ad-unit picks by funnel stage so the Sim workflow
      // (and the downstream image generator) can see exactly which units
      // were selected, how many of each, and which reference image was
      // chosen for each one.
      const stageMap = new Map<string, { stageId: string; stageName: string; units: any[] }>();
      for (const sel of adUnitSelections) {
        const stage = draft.funnelStrategy?.funnel_stages?.find((s) => s.id === sel.funnelStageId);
        const key = sel.funnelStageId;
        if (!stageMap.has(key)) {
          stageMap.set(key, {
            stageId: sel.funnelStageId,
            stageName: stage?.name ?? sel.funnelStageId,
            units: [],
          });
        }
        stageMap.get(key)!.units.push({
          ad_unit_id: sel.adUnitId,
          ad_unit_slug: sel.adUnit?.slug,
          ad_unit_name: sel.adUnit?.name,
          quantity: sel.quantity,
          selected_image_url: sel.selectedImageUrl ?? null,
        });
      }
      const adUnitSelectionsPayload = Array.from(stageMap.values());

      const res = await fetch("/api/generate-media-allocation", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          product: draft.product ?? null,
          persona: draft.persona ?? null,
          narrative_opportunity: draft.narrativeOpportunity ?? null,
          narrative_angles: draft.narrativeAngles ?? [],
          funnel_strategy: draft.funnelStrategy ?? null,
          media_mix: draft.mediaMix ?? null,
          campaign_plan: null,
          planner_persona: draft.plannerPersona ?? null,
          ad_unit_selections: adUnitSelectionsPayload,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as any).message ?? `Server returned ${res.status}`);
      }
      const data = await res.json();
      // Sim returns { output: { content: "..." } } or the allocation directly
      let allocation: MediaAllocation | null = null;
      if (data?.allocation_plan) {
        allocation = data as MediaAllocation;
      } else if (data?.output?.content) {
        try {
          const match = data.output.content.match(/```(?:json)?\s*([\s\S]*?)```/s);
          allocation = JSON.parse(match ? match[1] : data.output.content);
        } catch { allocation = null; }
      } else if (data?.output?.allocation_plan) {
        allocation = data.output as MediaAllocation;
      }
      if (!allocation?.allocation_plan) throw new Error("No allocation data returned from Sim Studio");
      onUpdateMediaAllocation(allocation);
      toast({ title: "Ad allocation generated", description: "Review the allocation below, then select your ad formats." });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: "Allocation generation failed", description: msg, variant: "destructive" });
    } finally {
      setIsGeneratingAllocation(false);
    }
  }, [draft, onUpdateMediaAllocation, toast]);

  const { data: rawAdUnitsAll = [], isLoading } = useQuery<any[]>({
    queryKey: [`/api/brands/${brandId}/ad-units`],
    enabled: !!brandId,
  });
  // The standard campaign wizard plans static/video ad placements; UGC ad
  // units have their own dedicated AI UGC Campaigns flow and shouldn't appear
  // here as selectable options.
  const rawAdUnits = useMemo(
    () => rawAdUnitsAll.filter((u: any) => u?.formatType !== "ugc"),
    [rawAdUnitsAll],
  );

  const currentStage = STAGES[stageIndex];

  const selectedIds = useMemo(
    () => new Set(adUnitSelections.map((s) => s.adUnitId)),
    [adUnitSelections]
  );

  const unitMatchesStage = useCallback((unit: any, stageId: string) => {
    return (unit.funnelStage || "") === stageId || (unit.secondaryFunnel || "") === stageId;
  }, []);

  const selectionsByStage = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const sel of adUnitSelections) {
      const stageId = sel.funnelStageId || "Awareness";
      counts[stageId] = (counts[stageId] || 0) + 1;
    }
    return counts;
  }, [adUnitSelections]);

  const stageUnits = useMemo(
    () => showAllUnits ? rawAdUnits : rawAdUnits.filter((u) => unitMatchesStage(u, currentStage?.id)),
    [rawAdUnits, currentStage, unitMatchesStage, showAllUnits]
  );

  function isSelectedForCurrentStage(id: string) {
    return adUnitSelections.some(
      (s) => s.adUnitId === id && s.funnelStageId === currentStage.id
    );
  }

  function toggle(raw: any) {
    const id = raw.id;
    if (isSelectedForCurrentStage(id)) {
      onUpdateSelections(
        adUnitSelections.filter(
          (s) => !(s.adUnitId === id && s.funnelStageId === currentStage.id)
        )
      );
    } else {
      const wizardUnit = realToWizardUnit(raw);
      const images: string[] = exampleImageUrls(raw.exampleImages);
      onUpdateSelections([
        ...adUnitSelections,
        {
          adUnitId: id,
          adUnit: wizardUnit,
          funnelStageId: currentStage.id,
          quantity: 1,
          selectedImageUrl: images[0] ?? null,
        },
      ]);
    }
    if (isStale) onClearStale();
  }

  function setImageForSelection(adUnitId: string, stageId: string, url: string) {
    onUpdateSelections(
      adUnitSelections.map((s) =>
        s.adUnitId === adUnitId && s.funnelStageId === stageId
          ? { ...s, selectedImageUrl: url }
          : s
      )
    );
    if (isStale) onClearStale();
  }

  function getSelectedImageFor(adUnitId: string, stageId: string): string | null {
    const sel = adUnitSelections.find(
      (s) => s.adUnitId === adUnitId && s.funnelStageId === stageId
    );
    return sel?.selectedImageUrl ?? null;
  }

  function selectAll() {
    const alreadySelected = new Set(
      adUnitSelections
        .filter((s) => s.funnelStageId === currentStage.id)
        .map((s) => s.adUnitId)
    );
    const additional: AdUnitSelection[] = stageUnits
      .filter((u) => !alreadySelected.has(u.id))
      .map((raw) => ({
        adUnitId: raw.id,
        adUnit: realToWizardUnit(raw),
        funnelStageId: currentStage.id,
        quantity: 1,
      }));
    onUpdateSelections([...adUnitSelections, ...additional]);
    if (isStale) onClearStale();
  }

  function clearStage() {
    onUpdateSelections(
      adUnitSelections.filter((s) => s.funnelStageId !== currentStage.id)
    );
  }

  // Smart pick: one-click-select the curated recommended formats for the
  // active stage — units whose PRIMARY funnel stage matches this stage —
  // capped by the per-stage planned static target (when an AI media
  // allocation exists) and never overshooting the remaining global budget.
  function smartPick() {
    const remainingBudget = savedStaticUnits - adUnitSelections.length;
    if (remainingBudget <= 0) {
      setSmartPickMessage(
        `Budget reached — ${adUnitSelections.length} / ${savedStaticUnits} static ad units selected. Raise Total Static Ad Units to add more.`
      );
      return;
    }

    const alreadySelected = new Set(
      adUnitSelections
        .filter((s) => s.funnelStageId === currentStage.id)
        .map((s) => s.adUnitId)
    );
    const recommended = rawAdUnits.filter(
      (u) => (u.funnelStage || "") === currentStage.id
    );
    let candidates = recommended.filter((u) => !alreadySelected.has(u.id));

    // Cap by the per-stage planned static target whenever an AI allocation
    // exists — including a planned target of 0, which means add nothing for
    // this stage.
    const hasAllocationTarget = stageStaticTarget !== null;
    if (hasAllocationTarget) {
      const targetRemaining = Math.max(0, (stageStaticTarget ?? 0) - alreadySelected.size);
      candidates = candidates.slice(0, targetRemaining);
    }

    const stoppedByBudget = candidates.length > remainingBudget;
    const toAdd = candidates.slice(0, remainingBudget);

    if (toAdd.length === 0) {
      if (recommended.length === 0) {
        setSmartPickMessage(`No recommended formats available for ${currentStage.label}.`);
      } else if (hasAllocationTarget && (stageStaticTarget ?? 0) <= alreadySelected.size) {
        setSmartPickMessage(
          (stageStaticTarget ?? 0) === 0
            ? `No static formats planned for ${currentStage.label} in the current allocation.`
            : `Stage target reached — ${alreadySelected.size} / ${stageStaticTarget} planned static formats already selected for ${currentStage.label}.`
        );
      } else if (stoppedByBudget) {
        setSmartPickMessage(
          `Budget reached — ${adUnitSelections.length} / ${savedStaticUnits} static ad units selected. Raise Total Static Ad Units to add more.`
        );
      } else {
        setSmartPickMessage(`All recommended formats for ${currentStage.label} are already selected.`);
      }
      return;
    }

    const additions: AdUnitSelection[] = toAdd.map((raw) => {
      const images = exampleImageUrls(raw.exampleImages);
      return {
        adUnitId: raw.id,
        adUnit: realToWizardUnit(raw),
        funnelStageId: currentStage.id,
        quantity: 1,
        selectedImageUrl: images[0] ?? null,
      };
    });
    onUpdateSelections([...adUnitSelections, ...additions]);
    if (isStale) onClearStale();

    let msg = `Smart pick added ${toAdd.length} recommended format${toAdd.length !== 1 ? "s" : ""} for ${currentStage.label}.`;
    if (stoppedByBudget) {
      msg += ` Budget reached at ${adUnitSelections.length + toAdd.length} / ${savedStaticUnits}.`;
    }
    setSmartPickMessage(msg);
  }

  function selectAllAcrossStages() {
    const existingPairs = new Set(
      adUnitSelections.map((s) => `${s.adUnitId}|${s.funnelStageId}`)
    );
    const additions: AdUnitSelection[] = [];
    for (const stage of STAGES) {
      const units = showAllUnits
        ? rawAdUnits
        : rawAdUnits.filter((u) => unitMatchesStage(u, stage.id));
      for (const raw of units) {
        const key = `${raw.id}|${stage.id}`;
        if (existingPairs.has(key)) continue;
        existingPairs.add(key);
        const images: string[] = exampleImageUrls(raw.exampleImages);
        additions.push({
          adUnitId: raw.id,
          adUnit: realToWizardUnit(raw),
          funnelStageId: stage.id,
          quantity: 1,
          selectedImageUrl: images[0] ?? null,
        });
      }
    }
    if (additions.length === 0) return;
    onUpdateSelections([...adUnitSelections, ...additions]);
    if (isStale) onClearStale();
  }

  function clearAllStages() {
    if (adUnitSelections.length === 0) return;
    onUpdateSelections([]);
    if (isStale) onClearStale();
  }

  function goNext() {
    setSmartPickMessage(null);
    if (stageIndex < STAGES.length - 1) {
      setStageIndex(stageIndex + 1);
    } else {
      setShowSummary(true);
    }
  }

  function goPrev() {
    setSmartPickMessage(null);
    if (showSummary) {
      setShowSummary(false);
    } else if (stageIndex > 0) {
      setStageIndex(stageIndex - 1);
    }
  }

  const stageSelectedCount = selectionsByStage[currentStage?.id] ?? 0;
  const allStageSelected = stageUnits.length > 0 && stageUnits.every((u) => isSelectedForCurrentStage(u.id));

  // Global budget: total selections across every stage vs the planned
  // static-unit budget (Total Static Ad Units).
  const totalSelected = adUnitSelections.length;
  const budget = savedStaticUnits;
  const atBudget = totalSelected === budget;
  const overBudget = totalSelected > budget;

  const allAcrossStagesSelected = useMemo(() => {
    if (rawAdUnits.length === 0) return false;
    const selectedKeys = new Set(
      adUnitSelections.map((s) => `${s.adUnitId}|${s.funnelStageId}`)
    );
    let totalRequired = 0;
    for (const stage of STAGES) {
      const units = showAllUnits
        ? rawAdUnits
        : rawAdUnits.filter((u) => unitMatchesStage(u, stage.id));
      for (const raw of units) {
        totalRequired++;
        if (!selectedKeys.has(`${raw.id}|${stage.id}`)) return false;
      }
    }
    return totalRequired > 0;
  }, [adUnitSelections, rawAdUnits, showAllUnits, unitMatchesStage]);

  const allocationTotals = useMemo(() => {
    if (!mediaAllocation?.allocation_plan) return null;
    const p = mediaAllocation.allocation_plan;
    const sum = (s: any) => (s?.ad_types?.static ?? 0) + (s?.ad_types?.video ?? 0) + (s?.ad_types?.carousel ?? 0);
    const st = (s: any) => s?.ad_types?.static ?? 0;
    const stages: Record<string, number> = {
      Awareness: sum(p.awareness),
      Knowledge: sum(p.interest),
      Liking: sum(p.consideration),
      Preference: sum(p.intent),
      Conviction: sum(p.evaluation),
      Purchase: sum(p.purchase),
    };
    const statics: Record<string, number> = {
      Awareness: st(p.awareness),
      Knowledge: st(p.interest),
      Liking: st(p.consideration),
      Preference: st(p.intent),
      Conviction: st(p.evaluation),
      Purchase: st(p.purchase),
    };
    return {
      stages,
      statics,
      total: Object.values(stages).reduce((a, b) => a + b, 0),
    };
  }, [mediaAllocation]);

  const stageStaticTarget = allocationTotals?.statics?.[currentStage?.id] ?? null;

  if (showSummary) {
    return (
      <div className="space-y-6" data-testid="step-ad-unit-selection">
        <AdUnitDetailModal
          raw={previewUnit}
          isSelected={previewUnit ? isSelectedForCurrentStage(previewUnit.id) : false}
          onToggle={() => { if (previewUnit) toggle(previewUnit); }}
          onClose={() => setPreviewUnit(null)}
          stageConfig={previewUnit ? STAGES.find((s) => s.id === (previewUnit.funnelStage || "")) ?? null : null}
          selectedImageUrl={previewUnit ? getSelectedImageFor(previewUnit.id, currentStage.id) : null}
          onSelectImage={(url) => { if (previewUnit) setImageForSelection(previewUnit.id, currentStage.id, url); }}
        />

        {isStale && adUnitSelections.length > 0 && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-yellow-500/30 bg-yellow-500/5 text-yellow-600 dark:text-yellow-400">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <p className="text-sm">Upstream inputs changed. Review your selections.</p>
          </div>
        )}

        <SelectionSummary
          allAdUnits={rawAdUnits}
          adUnitSelections={adUnitSelections}
          onEdit={(idx) => { setStageIndex(idx); setShowSummary(false); }}
        />

        <div className="flex items-center justify-between pt-2">
          <Button variant="outline" onClick={goPrev} className="rounded-full gap-2" data-testid="btn-stage-back">
            <ArrowLeft className="h-4 w-4" />
            Back to {STAGES[STAGES.length - 1].label}
          </Button>
          <div className="text-sm text-muted-foreground font-medium">
            {selectedIds.size} unit{selectedIds.size !== 1 ? "s" : ""} selected
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="step-ad-unit-selection">
      <AdUnitDetailModal
        raw={previewUnit}
        isSelected={previewUnit ? isSelectedForCurrentStage(previewUnit.id) : false}
        onToggle={() => { if (previewUnit) toggle(previewUnit); }}
        onClose={() => setPreviewUnit(null)}
        stageConfig={currentStage}
        selectedImageUrl={previewUnit ? getSelectedImageFor(previewUnit.id, currentStage.id) : null}
        onSelectImage={(url) => { if (previewUnit) setImageForSelection(previewUnit.id, currentStage.id, url); }}
      />

      {/* Header */}
      <div>
        <h2 className="text-xl font-bold tracking-tight">Select Ad Units</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Choose ad formats for each funnel stage, one stage at a time.
        </p>
      </div>

      {/* Media mix — total static units to plan for this campaign */}
      <div className="rounded-xl border border-border/40 bg-card px-4 py-3 flex items-center justify-between gap-4 flex-wrap" data-testid="card-media-mix">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Image className="h-4 w-4 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold">Total Static Ad Units</p>
            <p className="text-xs text-muted-foreground">
              How many static ads to plan across the funnel. Defaults to {DEFAULT_STATIC_UNITS}.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={1}
            max={100}
            value={staticUnitsDraft}
            onChange={(e) => setStaticUnitsDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSaveStaticUnits();
              }
            }}
            className="w-24 h-9 text-right tabular-nums"
            data-testid="input-static-units"
          />
          <Button
            type="button"
            size="sm"
            variant={isDraftDirty ? "default" : "outline"}
            onClick={handleSaveStaticUnits}
            disabled={!isDraftValid || !isDraftDirty}
            data-testid="button-save-static-units"
          >
            Save
          </Button>
        </div>
      </div>

      {isStale && adUnitSelections.length > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-yellow-500/30 bg-yellow-500/5 text-yellow-600 dark:text-yellow-400">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <p className="text-sm">Upstream inputs changed. Review your selections and update if needed.</p>
        </div>
      )}

      <div className="flex items-center justify-between rounded-xl border border-border/40 bg-card px-4 py-3">
        <div className="flex items-center gap-2">
          <Switch
            checked={showAllUnits}
            onCheckedChange={setShowAllUnits}
            data-testid="switch-show-all-units"
          />
          <span className="text-sm font-medium">Show all ad units on every stage</span>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={allAcrossStagesSelected ? clearAllStages : selectAllAcrossStages}
            disabled={rawAdUnits.length === 0}
            className="h-7 text-xs text-primary hover:text-primary rounded-full"
            data-testid="btn-select-all-stages"
          >
            {allAcrossStagesSelected ? "Clear every ad on every stage" : "Select every ad on every stage"}
          </Button>
          <div
            className={`flex items-baseline gap-1.5 rounded-full border px-3 py-1 transition-colors ${
              overBudget
                ? "bg-red-500/10 border-red-500/25 text-red-600 dark:text-red-400"
                : atBudget
                ? "bg-emerald-500/10 border-emerald-500/25 text-emerald-600 dark:text-emerald-400"
                : "bg-primary/10 border-primary/20 text-primary"
            }`}
            title="Total static ad units selected across all stages, against your budget"
            data-testid="text-budget-counter"
          >
            <span className="text-sm font-bold tabular-nums">{totalSelected}</span>
            <span className="text-xs opacity-60">/</span>
            <span className="text-sm font-semibold tabular-nums opacity-80">{budget}</span>
            {overBudget && (
              <span className="text-[10px] font-semibold uppercase tracking-wide ml-1">over</span>
            )}
            {atBudget && (
              <span className="text-[10px] font-semibold uppercase tracking-wide ml-1">full</span>
            )}
          </div>
        </div>
      </div>

      {/* Stage stepper */}
      <StageStepper
        activeIndex={stageIndex}
        selectionsByStage={selectionsByStage}
        onNavigate={(idx) => { setSmartPickMessage(null); setShowSummary(false); setStageIndex(idx); }}
      />

      {/* Smart pick confirmation banner */}
      {smartPickMessage && (
        <div
          className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl border border-primary/25 bg-primary/[0.06] text-primary"
          data-testid="banner-smart-pick"
        >
          <Sparkles className="h-4 w-4 flex-shrink-0" />
          <p className="text-sm font-medium flex-1">{smartPickMessage}</p>
          <button
            onClick={() => setSmartPickMessage(null)}
            className="p-0.5 rounded-md hover:bg-primary/10 transition-colors"
            data-testid="btn-dismiss-smart-pick"
            aria-label="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Stage header */}
      <div className={`rounded-2xl px-5 py-4 border ${currentStage.bgTint}`}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${currentStage.iconBg}`}>
              <currentStage.Icon className={`h-5 w-5 ${currentStage.textColor}`} />
            </div>
            <div>
              <p className="font-semibold text-sm">{currentStage.label}</p>
              <p className="text-xs text-muted-foreground">{currentStage.description}</p>
            </div>
          </div>
          {stageSelectedCount > 0 && (
            <Badge className="text-xs rounded-full bg-primary/10 text-primary border-primary/20 font-medium px-3">
              {stageSelectedCount} selected
            </Badge>
          )}
        </div>
      </div>

      {/* Select all / clear row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-xs text-muted-foreground">
            {stageUnits.length} format{stageUnits.length !== 1 ? "s" : ""} {showAllUnits ? "available" : `available for ${currentStage.shortLabel}`}
          </p>
          {stageStaticTarget !== null && stageStaticTarget > 0 && (
            <>
              <span className="text-xs text-muted-foreground/40">·</span>
              <span className="text-xs font-medium text-primary/80" data-testid="text-static-target">
                {stageStaticTarget} static{stageStaticTarget !== 1 ? "s" : ""} planned
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={smartPick}
            disabled={stageUnits.length === 0}
            className="h-7 gap-1.5 text-xs rounded-full border-primary/30 text-primary hover:text-primary hover:bg-primary/5"
            data-testid="btn-smart-pick">
            <Wand2 className="h-3.5 w-3.5" />
            Smart pick
          </Button>
          {stageSelectedCount > 0 && (
            <Button variant="ghost" size="sm" onClick={clearStage}
              className="h-7 gap-1.5 text-xs text-muted-foreground hover:text-foreground rounded-full"
              data-testid="btn-clear-stage">
              <X className="h-3.5 w-3.5" />
              Clear {currentStage.shortLabel}
            </Button>
          )}
          <Button
            variant="ghost" size="sm"
            onClick={allStageSelected ? clearStage : selectAll}
            disabled={stageUnits.length === 0}
            className="h-7 gap-1.5 text-xs text-primary hover:text-primary rounded-full"
            data-testid="btn-select-all">
            <Check className="h-3.5 w-3.5" />
            {allStageSelected ? `Deselect ${currentStage.shortLabel}` : `Select all in ${currentStage.shortLabel}`}
          </Button>
        </div>
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-28 rounded-2xl bg-muted/40 animate-pulse" />
          ))}
        </div>
      ) : stageUnits.length === 0 ? (
        <div className="text-center py-16 rounded-2xl border border-dashed border-border">
          <currentStage.Icon className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No ad units found for this stage</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {stageUnits.map((raw) => (
            <AdUnitCard
              key={raw.id}
              raw={raw}
              isSelected={isSelectedForCurrentStage(raw.id)}
              onToggle={() => toggle(raw)}
              onPreview={() => setPreviewUnit(raw)}
              stageConfig={currentStage}
              isSecondary={(raw.funnelStage || "") !== currentStage.id && (raw.secondaryFunnel || "") === currentStage.id}
              selectedImageUrl={getSelectedImageFor(raw.id, currentStage.id)}
            />
          ))}
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between pt-2 border-t border-border">
        <Button
          variant="outline"
          onClick={goPrev}
          disabled={stageIndex === 0}
          className="rounded-full gap-2"
          data-testid="btn-stage-prev"
        >
          <ArrowLeft className="h-4 w-4" />
          {stageIndex > 0 ? STAGES[stageIndex - 1].label : "Back"}
        </Button>

        <div className="flex items-center gap-1.5">
          {STAGES.map((_, i) => (
            <div key={i} className={`rounded-full transition-all ${
              i === stageIndex ? "w-4 h-1.5 bg-primary" : i < stageIndex ? "w-1.5 h-1.5 bg-primary/40" : "w-1.5 h-1.5 bg-muted-foreground/20"
            }`} />
          ))}
        </div>

        <Button
          onClick={goNext}
          className="rounded-full gap-2"
          data-testid="btn-stage-next"
        >
          {stageIndex < STAGES.length - 1 ? (
            <>{STAGES[stageIndex + 1].label}<ArrowRight className="h-4 w-4" /></>
          ) : (
            <>View Summary<ArrowRight className="h-4 w-4" /></>
          )}
        </Button>
      </div>
    </div>
  );
}
